import type { DomainDto, MailboxSecretDto } from "@bafe/api-client";
import { ArrowSquareOutIcon, CheckCircleIcon } from "@phosphor-icons/react";
import {
	Button,
	Card,
	Field,
	InlineNotice,
	Input,
	PageHeader,
	Section,
	Select,
	SkeletonRows,
	useToast,
} from "@bafe/ui";
import {
	checkMailboxPassword,
	formatBytes,
	generateMailboxPassword,
	parseStorageToBytes,
	validateLocalPart,
	validateMailboxQuota,
} from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ErrorPanel } from "../../components/ErrorPanel";
import { LinkButton } from "../../components/LinkButton";
import { PasswordField } from "../../components/PasswordField";
import { QueryState } from "../../components/QueryState";
import { SecretOnce } from "../../components/SecretOnce";
import { api } from "../../lib/api";
import { rememberWebmailOpenAccount } from "../../lib/webmail";
import { fieldError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";

type QuotaUnit = "MB" | "GB" | "TB";

function domainAllowsMailbox(domain: DomainDto): boolean {
	return (
		domain.emailEnabled &&
		domain.ownershipVerifiedAt !== null &&
		domain.status !== "removing" &&
		domain.status !== "removed" &&
		domain.status !== "suspended"
	);
}

export function MailboxNew() {
	const domains = useQuery({ queryKey: queryKeys.domains, queryFn: () => api.listDomains() });

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Create a mailbox"
				description="A real email account with its own password, on one of your domains."
				breadcrumb={
					<Link
						to="/mail/mailboxes"
						className="text-sm text-(--text-secondary) underline underline-offset-2 hover:text-(--text-primary)"
					>
						Mailboxes
					</Link>
				}
			/>

			<QueryState
				query={domains}
				loading={<SkeletonRows rows={4} />}
				errorTitle="We could not load your domains."
			>
				{(list) => <CreateMailbox domains={list} />}
			</QueryState>
		</div>
	);
}

function CreateMailbox({ domains }: { domains: DomainDto[] }) {
	const [searchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const mailboxDomains = domains.filter(domainAllowsMailbox);
	const preselected = searchParams.get("domain");

	const [domainId, setDomainId] = useState(
		() =>
			preselected && mailboxDomains.some((d) => d.id === preselected)
				? preselected
				: (mailboxDomains[0]?.id ?? ""),
	);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [localPart, setLocalPart] = useState("");
	const [password, setPassword] = useState("");
	const [quotaValue, setQuotaValue] = useState("");
	const [quotaUnit, setQuotaUnit] = useState<QuotaUnit>("GB");
	const [submitted, setSubmitted] = useState(false);
	const [created, setCreated] = useState<MailboxSecretDto | null>(null);

	const domain = mailboxDomains.find((item) => item.id === domainId) ?? null;
	const localPartResult = validateLocalPart(localPart);
	const emailAddress = domain ? `${localPartResult.normalized}@${domain.domainName}` : "";

	const quotaBytes =
		quotaValue.trim() === "" ? null : parseStorageToBytes(Number(quotaValue), quotaUnit);
	const quotaCheck = validateMailboxQuota(
		quotaValue.trim() === "" ? null : Number.isFinite(Number(quotaValue)) ? quotaBytes : Number.NaN,
		0,
	);
	const passwordCheck = checkMailboxPassword(password, emailAddress);

	const localPartError =
		submitted && !localPartResult.ok ? (localPartResult.message ?? "Enter the part before the @.") : null;
	const quotaError =
		submitted && !quotaCheck.allowed ? (quotaCheck.message ?? null) : null;
	const passwordError = submitted && !passwordCheck.ok ? (passwordCheck.message ?? null) : null;
	const formValid =
		localPartResult.ok && passwordCheck.ok && quotaCheck.allowed && domainId !== "";

	const create = useMutation({
		mutationFn: () =>
			api.createMailbox({
				domainId,
				localPart: localPartResult.normalized,
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				password,
				quotaBytes,
			}),
		onSuccess: (secret) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailboxes() });
			void queryClient.invalidateQueries({ queryKey: queryKeys.storage });
			void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
			setCreated(secret);
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitted(true);
		if (!formValid) return;
		create.mutate();
	}

	function startAnother() {
		setCreated(null);
		setFirstName("");
		setLastName("");
		setLocalPart("");
		setPassword("");
		setQuotaValue("");
		setSubmitted(false);
		create.reset();
	}

	if (created) {
		return <MailboxCreated secret={created} onCreateAnother={startAnother} />;
	}

	if (mailboxDomains.length === 0) {
		const hasAnyDomain = domains.some(
			(item) => item.status !== "removing" && item.status !== "removed" && item.status !== "suspended",
		);
		return (
			<Card>
				<InlineNotice
					tone="info"
					title={hasAnyDomain ? "No domain is ready for email accounts" : "Add a domain first"}
				>
					{hasAnyDomain
						? "Turn on email for a domain and verify ownership before creating a mailbox."
						: "A mailbox lives on a domain you own. Add one and Key will walk you through the DNS."}
				</InlineNotice>
				<div className="mt-4">
					<LinkButton to="/manage/domains/new" variant="primary">
						{hasAnyDomain ? "Review domains" : "Add a domain"}
					</LinkButton>
				</div>
			</Card>
		);
	}

	return (
		<form className="flex max-w-2xl flex-col gap-6" onSubmit={submit} noValidate>
			<Section title="Who is this for?">
				<div className="grid gap-4 sm:grid-cols-2">
					<Field label="First name" error={fieldError(create.error, "firstName")}>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={firstName}
								autoComplete="off"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setFirstName(event.target.value)}
							/>
						)}
					</Field>

					<Field label="Last name" error={fieldError(create.error, "lastName")}>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={lastName}
								autoComplete="off"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setLastName(event.target.value)}
							/>
						)}
					</Field>
				</div>
			</Section>

			<Section title="Email address">
				<div className="grid gap-4 sm:grid-cols-2 sm:items-end">
					<Field
						label="Address"
						error={localPartError ?? fieldError(create.error, "localPart")}
						required
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={localPart}
								required
								autoComplete="off"
								autoCapitalize="none"
								spellCheck={false}
								placeholder="syahir"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setLocalPart(event.target.value)}
							/>
						)}
					</Field>

					<Field label="Domain" error={fieldError(create.error, "domainId")} required>
						{({ id, describedBy, invalid }) => (
							<Select
								id={id}
								value={domainId}
								required
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setDomainId(event.target.value)}
							>
								{mailboxDomains.map((item) => (
									<option key={item.id} value={item.id}>
										{item.domainName}
									</option>
								))}
							</Select>
						)}
					</Field>
				</div>
				<p className="text-xs text-(--text-muted)">The part before the @.</p>

				{emailAddress && localPartResult.ok && (
					<p className="text-sm text-(--text-secondary)">
						This creates <span className="text-dns text-(--text-primary)">{emailAddress}</span>.
					</p>
				)}

				{domain && domain.emailEnabled && domain.mailReadyAt === null && (
					<InlineNotice tone="caution" title="This domain is not receiving mail yet">
						You can create the account now. It starts sending and receiving once {domain.domainName} has
						its DNS records in place.
					</InlineNotice>
				)}
			</Section>

			<Section title="Password">
				<PasswordField
					label="Mailbox password"
					value={password}
					onChange={setPassword}
					error={passwordError ?? fieldError(create.error, "password")}
					help="This is what the mail app on a phone or laptop will use. It is separate from your Key account password."
					autoComplete="new-password"
					required
					action={
						<button
							type="button"
							className="text-xs font-medium text-(--color-brand-700) underline underline-offset-2"
							onClick={() => {
								const generated = generateMailboxPassword();
								setPassword(generated);
								toast({ tone: "info", message: "Generated a strong password. Copy it after you save." });
							}}
						>
							Generate secure password
						</button>
					}
				/>
			</Section>

			<Section title="Storage limit">
				<div className="grid gap-4 sm:grid-cols-[1fr_8rem] sm:items-end">
					<Field
						label="Limit for this mailbox (optional)"
						error={quotaError}
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								type="number"
								min={1}
								inputMode="numeric"
								value={quotaValue}
								placeholder="No limit"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setQuotaValue(event.target.value)}
							/>
						)}
					</Field>

					<Field label="Unit">
						{({ id }) => (
							<Select
								id={id}
								value={quotaUnit}
								onChange={(event) => setQuotaUnit(event.target.value as QuotaUnit)}
							>
								<option value="MB">MB</option>
								<option value="GB">GB</option>
								<option value="TB">TB</option>
							</Select>
						)}
					</Field>
				</div>
				<p className="text-xs text-(--text-muted)">
					An administrative ceiling inside your account&apos;s shared storage. It does not buy extra
					storage, and leaving it blank is fine.
				</p>

				{quotaBytes !== null && quotaCheck.allowed && (
					<p className="text-sm text-(--text-secondary)">
						This mailbox will stop accepting mail above {formatBytes(quotaBytes)}, or sooner if your
						account runs out of storage.
					</p>
				)}
			</Section>

			{create.isError && <ErrorPanel error={create.error} title="We could not create this mailbox." />}

			{submitted && !formValid && !create.isPending && (
				<InlineNotice tone="caution" title="A few fields need attention">
					Check the address and mailbox password above — errors appear in red under each field.
				</InlineNotice>
			)}

			<div className="flex flex-wrap gap-2">
				<Button
					type="submit"
					variant="primary"
					pending={create.isPending}
					pendingLabel="Creating the mailbox…"
				>
					Create mailbox
				</Button>
				<LinkButton to="/mail/mailboxes" variant="ghost">
					Cancel
				</LinkButton>
			</div>
		</form>
	);
}

function MailboxCreated({
	secret,
	onCreateAnother,
}: {
	secret: MailboxSecretDto;
	onCreateAnother: () => void;
}) {
	const { toast } = useToast();

	const openWebmail = useMutation({
		mutationFn: () => api.createWebmailHandoff(secret.mailboxId),
		onSuccess: (result) => {
			const match = /\/mail\/([^/?]+)/.exec(result.url);
			if (match?.[1]) rememberWebmailOpenAccount(decodeURIComponent(match[1]));
			window.open(result.url, "_blank", "noopener,noreferrer");
		},
		onError: () => {
			toast({ tone: "error", message: "We could not open webmail just now. Try again in a moment." });
		},
	});

	return (
		<Section>
			<Card className="flex flex-col gap-5">
				<div className="flex items-start gap-3">
					<CheckCircleIcon
						className="mt-0.5 size-6 shrink-0 text-(--color-positive-600)"
						weight="fill"
						aria-hidden="true"
					/>
					<div className="flex flex-col gap-1">
						<h2 className="text-base font-semibold text-(--text-primary)">
							{secret.emailAddress} is ready.
						</h2>
						<p className="text-sm text-(--text-secondary)">
							It can send and receive straight away.
						</p>
					</div>
				</div>

				<SecretOnce
					title="Mailbox password"
					value={secret.password}
					label="Mailbox password"
					instruction="Copy it now and give it to whoever will use this mailbox."
				/>

				<div className="flex flex-wrap gap-2">
					<Button
						variant="primary"
						pending={openWebmail.isPending}
						pendingLabel="Opening…"
						onClick={() => openWebmail.mutate()}
						iconRight={<ArrowSquareOutIcon className="size-4" aria-hidden="true" />}
					>
						Open Webmail
					</Button>
					<LinkButton to={`/mail/mailboxes/${secret.mailboxId}?tab=devices`} variant="secondary">
						Set up on a device
					</LinkButton>
					<Button variant="ghost" onClick={onCreateAnother}>
						Create another mailbox
					</Button>
				</div>
			</Card>
		</Section>
	);
}
